import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import type { ListResponse } from '../../types'
import type { MiningSite } from './types'
import { SITE_DECISIONS } from './types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge, Textarea,
} from '../../components/ui'
import DownloadButton from '../../components/DownloadButton'
import { IdIcon, PlusIcon } from '../../components/icons'

const FILTERS = [
  { value: 'pending', label: 'Awaiting validation' },
  { value: '', label: 'All' },
  { value: 'validated', label: 'Validated' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'rejected', label: 'Rejected' },
]

const DECISION_TONE: Record<string, 'primary' | 'secondary' | 'danger'> = {
  validated: 'primary', flagged: 'secondary', rejected: 'danger',
}

export default function GovSites() {
  const qc = useQueryClient()
  const [status, setStatus] = useState('pending')
  const [openId, setOpenId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'sites', status],
    queryFn: () => api.get<ListResponse<MiningSite>>(
      `/gov/sites${status ? `?validation_status=${encodeURIComponent(status)}` : ''}`,
    ),
  })

  const validate = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: string }) =>
      api.post(`/gov/sites/${id}/validate`, { validation_status: decision, notes }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov'] })
      setOpenId(null)
      setNotes('')
    },
  })

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Site validation</h1>
          <p className="page-head__sub">
            Assess registered mining sites within your assigned area.
          </p>
        </div>
        {/* flexWrap so the Register button wraps under the exports on a
            phone instead of clipping off the right edge; desktop has room,
            so nothing moves there. */}
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <DownloadButton path="/api/v1/gov/exports/sites.xlsx" filename="sites_report.xlsx">
            Export Excel
          </DownloadButton>
          <DownloadButton path="/api/v1/gov/exports/sites.pdf" filename="sites_report.pdf">
            Export PDF
          </DownloadButton>
          <Link to="/sites/register">
            <Button icon={<PlusIcon />}>Register a site</Button>
          </Link>
        </div>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        {FILTERS.map((f) => (
          <Button key={f.label} size="sm"
                  variant={status === f.value ? 'primary' : 'secondary'}
                  onClick={() => setStatus(f.value)}>
            {f.label}
          </Button>
        ))}
      </div>

      {validate.isError && <Alert tone="danger">{(validate.error as Error).message}</Alert>}

      <Card>
        <CardHeader title="Sites"
                    sub={items.length ? `${items.length} site${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="74%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<IdIcon size={22} />}
                        title={status === 'pending' ? 'Nothing awaiting validation' : 'No matching sites'}
                        body={status === 'pending'
                          ? 'Every site in your area has been assessed.'
                          : 'Try a different filter.'} />
          ) : (
            <>
              {/* PWA tap rows - the 7-column table (with its inline Assess
                  expander) can't fit a phone; each row links to the site
                  detail screen, where the same assessment actions live.
                  Desktop keeps the table + inline expander unchanged. */}
              <div className="mobile-only">
                {items.map((s, i) => (
                  <Link key={s.id ?? i} to={s.id ? `/sites/${s.id}` : '#'} className="lrow">
                    <div className="lrow__main">
                      <div className="lrow__title">{s.name || s.id || '—'}</div>
                      <div className="lrow__sub">
                        {[s.district_id, titleCase(s.mineral_type), formatDate(s.registered_at)]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="lrow__end"><StatusBadge status={s.validation_status || 'pending'} /></div>
                  </Link>
                ))}
              </div>
            <div className="table-wrap desktop-only">
              <table className="table">
                <thead>
                  <tr>
                    <th>Site</th><th>Cooperative</th><th>District</th>
                    <th>Mineral</th><th>Registered</th><th>Status</th><th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((s, i) => {
                    const id = s.id ?? String(i)
                    const pending = (s.validation_status || 'pending') === 'pending'
                    return (
                      <Fragment key={id}>
                        <tr>
                          <td>{s.id ? <Link to={`/sites/${s.id}`} style={{ fontWeight: 600 }}>{s.name || s.id}</Link> : (s.name || '—')}</td>
                          <td>{s.org_id || '—'}</td>
                          <td>{s.district_id || '—'}</td>
                          <td>{titleCase(s.mineral_type)}</td>
                          <td>{formatDate(s.registered_at)}</td>
                          <td><StatusBadge status={s.validation_status || 'pending'} /></td>
                          <td className="table__num">
                            {s.id && pending && (
                              <Button size="sm" variant="secondary"
                                      onClick={() => { setOpenId(openId === id ? null : id); setNotes('') }}>
                                {openId === id ? 'Close' : 'Assess'}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {openId === id && s.id && (
                          <tr>
                            <td colSpan={7} style={{ background: 'var(--bg-sunken)' }}>
                              <div className="stack" style={{ padding: 'var(--sp-2) 0' }}>
                                {(s.latitude && s.longitude) && (
                                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                                    Coordinates: {s.latitude}, {s.longitude}
                                  </div>
                                )}
                                <Textarea label="Validation notes"
                                          placeholder="Reasoning for the decision — kept on the site record."
                                          value={notes}
                                          onChange={(e) => setNotes(e.target.value)} />
                                <div className="row">
                                  {SITE_DECISIONS.map((d) => (
                                    <Button key={d} size="sm"
                                            variant={DECISION_TONE[d]}
                                            loading={validate.isPending}
                                            onClick={() => validate.mutate({ id: s.id!, decision: d })}>
                                      {titleCase(d)}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}
